import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UserCog, Plus, Search, Pencil, Trash2, CheckCircle2, XCircle, Check, ChevronsUpDown, Tags, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { employee_id: '', spoc_type: '', department: '', locations: [], brand: 'All Brands', entity: 'All Entities', status: 'Active' };

export default function SpocManagementPage() {
  const { getAuthHeader, employee } = useAuth();
  const canManage = ['Super Admin', 'Admin Head'].includes(employee?.role);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ spoc_types: [], locations: [], brands: [], entities: [], departments: [] });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ q: '', spoc_type: 'all', department: 'all', location: 'all', brand: 'all', entity: 'all', status: 'all' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  // SPOC Types management
  const [typesOpen, setTypesOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState({ name: '', photo_trigger: false });
  const [editType, setEditType] = useState(null); // {type_id, name}

  const fetchTypes = useCallback(async () => {
    const r = await fetch(`${API}/spoc-types`, { headers: getAuthHeader() });
    if (r.ok) setTypes(await r.json());
  }, [getAuthHeader]);

  const openTypes = () => { fetchTypes(); setTypesOpen(true); };

  const addType = async () => {
    if (!newType.name.trim()) { toast.error('Enter a type name'); return; }
    const r = await fetch(`${API}/spoc-types`, { method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(newType) });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Failed'); return; }
    toast.success('SPOC type added');
    setNewType({ name: '', photo_trigger: false });
    fetchTypes(); fetchMeta();
  };

  const patchType = async (t, patch) => {
    const r = await fetch(`${API}/spoc-types/${t.type_id}`, { method: 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Failed'); return; }
    if (patch.name !== undefined) setEditType(null);
    fetchTypes(); fetchMeta(); fetchRows();
  };

  const removeType = async (t) => {
    const r = await fetch(`${API}/spoc-types/${t.type_id}`, { method: 'DELETE', headers: getAuthHeader() });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Failed'); return; }
    toast.success('SPOC type deleted');
    fetchTypes(); fetchMeta();
  };

  const fetchMeta = useCallback(async () => {
    const r = await fetch(`${API}/spoc-mappings/meta`, { headers: getAuthHeader() });
    if (r.ok) setMeta(await r.json());
  }, [getAuthHeader]);

  const fetchEmployees = useCallback(async () => {
    const r = await fetch(`${API}/employees`, { headers: getAuthHeader() });
    if (r.ok) setEmployees(await r.json());
  }, [getAuthHeader]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') p.set(k, v); });
      const r = await fetch(`${API}/spoc-mappings?${p.toString()}`, { headers: getAuthHeader() });
      if (r.ok) setRows(await r.json());
    } finally { setLoading(false); }
  }, [getAuthHeader, filters]);

  useEffect(() => { fetchMeta(); fetchEmployees(); }, [fetchMeta, fetchEmployees]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const selectedEmp = useMemo(() => employees.find(e => e.employee_id === form.employee_id), [employees, form.employee_id]);

  const openCreate = () => { setEditingId(null); setForm({ ...emptyForm, spoc_type: meta.spoc_types[0] || 'Visit & Guest' }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.mapping_id);
    setForm({ employee_id: row.employee_id, spoc_type: row.spoc_type, department: row.department || '', locations: row.locations || [], brand: row.brand, entity: row.entity, status: row.status });
    setDialogOpen(true);
  };

  const toggleLocation = (loc) => setForm(f => ({ ...f, locations: f.locations.includes(loc) ? f.locations.filter(l => l !== loc) : [...f.locations, loc] }));

  const save = async () => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    if (!form.spoc_type) { toast.error('Select a SPOC Type'); return; }
    if (!form.locations.length) { toast.error('Select at least one location'); return; }
    setSaving(true);
    try {
      const url = editingId ? `${API}/spoc-mappings/${editingId}` : `${API}/spoc-mappings`;
      const method = editingId ? 'PUT' : 'POST';
      const body = { ...form, department: form.department || (selectedEmp?.department || '') };
      const r = await fetch(url, { method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Save failed'); return; }
      toast.success(editingId ? 'SPOC mapping updated' : 'SPOC mapping created');
      setDialogOpen(false);
      fetchRows();
    } finally { setSaving(false); }
  };

  const setStatus = async (row, status) => {
    const r = await fetch(`${API}/spoc-mappings/${row.mapping_id}/status`, { method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    if (r.ok) { toast.success(`Mapping ${status === 'Active' ? 'activated' : 'deactivated'}`); fetchRows(); }
    else toast.error('Failed to update status');
  };

  const doDelete = async () => {
    const r = await fetch(`${API}/spoc-mappings/${deleteRow.mapping_id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('SPOC mapping deleted'); setDeleteRow(null); fetchRows(); }
    else toast.error('Delete failed');
  };

  if (!canManage) {
    return <Layout><div className="max-w-xl mx-auto mt-20 text-center" data-testid="spoc-access-denied">
      <UserCog className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
      <h2 className="text-xl font-semibold">Access Restricted</h2>
      <p className="text-muted-foreground mt-1">SPOC Management is available to Super Admin and Admin Head only.</p>
    </div></Layout>;
  }

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <Layout>
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="spoc-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-600/10 flex items-center justify-center"><UserCog className="h-6 w-6 text-indigo-600" /></div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">SPOC Management</h1>
              <p className="text-sm text-muted-foreground">Configure informed SPOCs for Client Factory Visit requests.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openTypes} data-testid="manage-types-btn"><Tags className="h-4 w-4 mr-1" />Manage Types</Button>
            <Button onClick={openCreate} data-testid="add-spoc-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" />Add SPOC</Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search employee / code / email" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} data-testid="spoc-search" />
            </div>
            <FilterSelect label="SPOC Type" value={filters.spoc_type} onChange={v => setFilters(f => ({ ...f, spoc_type: v }))} options={meta.spoc_types} testid="filter-spoc-type" />
            <FilterSelect label="Department" value={filters.department} onChange={v => setFilters(f => ({ ...f, department: v }))} options={meta.departments} testid="filter-department" />
            <FilterSelect label="Location" value={filters.location} onChange={v => setFilters(f => ({ ...f, location: v }))} options={meta.locations} testid="filter-location" />
            <FilterSelect label="Brand" value={filters.brand} onChange={v => setFilters(f => ({ ...f, brand: v }))} options={meta.brands} testid="filter-brand" />
            <FilterSelect label="Entity" value={filters.entity} onChange={v => setFilters(f => ({ ...f, entity: v }))} options={meta.entities} testid="filter-entity" />
            <FilterSelect label="Status" value={filters.status} onChange={v => setFilters(f => ({ ...f, status: v }))} options={['Active', 'Inactive']} testid="filter-status" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">SPOC Mappings ({rows.length})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>SPOC Type</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-10">No SPOC mappings found.</TableCell></TableRow>
                  ) : rows.map(row => (
                    <TableRow key={row.mapping_id} data-testid={`spoc-row-${row.mapping_id}`}>
                      <TableCell className="font-medium">{row.employee_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.employee_code}</TableCell>
                      <TableCell><Badge variant="outline">{row.spoc_type}</Badge></TableCell>
                      <TableCell>{row.department}</TableCell>
                      <TableCell className="text-xs">{(row.locations || []).join(', ')}</TableCell>
                      <TableCell>{row.brand}</TableCell>
                      <TableCell>{row.entity}</TableCell>
                      <TableCell><Badge className={row.status === 'Active' ? 'bg-emerald-600' : 'bg-slate-400'}>{row.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.created_by_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(row.created_at)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`edit-spoc-${row.mapping_id}`}><Pencil className="h-4 w-4" /></Button>
                        {row.status === 'Active'
                          ? <Button size="icon" variant="ghost" onClick={() => setStatus(row, 'Inactive')} title="Deactivate" data-testid={`deactivate-spoc-${row.mapping_id}`}><XCircle className="h-4 w-4 text-amber-600" /></Button>
                          : <Button size="icon" variant="ghost" onClick={() => setStatus(row, 'Active')} title="Activate" data-testid={`activate-spoc-${row.mapping_id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>}
                        <Button size="icon" variant="ghost" onClick={() => setDeleteRow(row)} data-testid={`delete-spoc-${row.mapping_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="spoc-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit SPOC Mapping' : 'Add SPOC Mapping'}</DialogTitle>
            <DialogDescription>SPOCs are informed on matching Client Factory Visit requests. They can view & comment but never own the ticket.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Employee searchable dropdown */}
            <div>
              <Label>Employee *</Label>
              <Popover open={empPickerOpen} onOpenChange={setEmpPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between mt-1" data-testid="spoc-employee-trigger">
                    {selectedEmp ? `${selectedEmp.full_name} (${selectedEmp.employee_code})` : 'Select employee from Employee Master'}
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search by name or code..." />
                    <CommandList>
                      <CommandEmpty>No employee found.</CommandEmpty>
                      <CommandGroup>
                        {employees.map(e => (
                          <CommandItem key={e.employee_id} value={`${e.full_name} ${e.employee_code} ${e.email}`} onSelect={() => { setForm(f => ({ ...f, employee_id: e.employee_id, department: e.department || f.department })); setEmpPickerOpen(false); }} data-testid={`spoc-emp-option-${e.employee_id}`}>
                            <Check className={`mr-2 h-4 w-4 ${form.employee_id === e.employee_id ? 'opacity-100' : 'opacity-0'}`} />
                            <span>{e.full_name} <span className="text-xs text-muted-foreground">({e.employee_code})</span></span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedEmp && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs bg-muted/40 rounded-lg p-3" data-testid="spoc-emp-info">
                  <div><span className="text-muted-foreground">Name:</span> {selectedEmp.full_name}</div>
                  <div><span className="text-muted-foreground">Code:</span> {selectedEmp.employee_code}</div>
                  <div><span className="text-muted-foreground">Department:</span> {selectedEmp.department}</div>
                  <div><span className="text-muted-foreground">Location:</span> {selectedEmp.location || '—'}</div>
                  <div><span className="text-muted-foreground">Entity:</span> {selectedEmp.entity || '—'}</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>SPOC Type *</Label>
                <Select value={form.spoc_type} onValueChange={v => setForm(f => ({ ...f, spoc_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="spoc-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{meta.spoc_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Select value={form.department} onValueChange={v => setForm(f => ({ ...f, department: v }))}>
                  <SelectTrigger className="mt-1" data-testid="spoc-department-select"><SelectValue placeholder="From employee" /></SelectTrigger>
                  <SelectContent>{meta.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Location * (multi-select)</Label>
              <div className="flex flex-wrap gap-2 mt-1 p-3 border rounded-lg bg-muted/20">
                {meta.locations.map(loc => {
                  const on = form.locations.includes(loc);
                  return <button type="button" key={loc} onClick={() => toggleLocation(loc)} data-testid={`spoc-loc-${loc}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background hover:bg-muted'}`}>{loc}</button>;
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Brand</Label>
                <Select value={form.brand} onValueChange={v => setForm(f => ({ ...f, brand: v }))}>
                  <SelectTrigger className="mt-1" data-testid="spoc-brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Entity</Label>
                <Select value={form.entity} onValueChange={v => setForm(f => ({ ...f, entity: v }))}>
                  <SelectTrigger className="mt-1" data-testid="spoc-entity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.entities.map(en => <SelectItem key={en} value={en}>{en}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1" data-testid="spoc-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="spoc-save-btn">{saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent data-testid="spoc-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SPOC mapping?</AlertDialogTitle>
            <AlertDialogDescription>This removes the mapping for {deleteRow?.employee_name}. Historical notifications are preserved. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage SPOC Types dialog */}
      <Dialog open={typesOpen} onOpenChange={setTypesOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="spoc-types-dialog">
          <DialogHeader>
            <DialogTitle>Manage SPOC Types</DialogTitle>
            <DialogDescription>Add, rename, activate/deactivate SPOC types. Mark a type as a "photography trigger" so it is informed only when a visit needs Photography/Videography. Renaming updates existing mappings.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {types.map(t => (
              <div key={t.type_id} className="flex items-center gap-2 border rounded-lg p-3" data-testid={`type-row-${t.type_id}`}>
                <div className="flex-1 min-w-0">
                  {editType?.type_id === t.type_id ? (
                    <div className="flex gap-2">
                      <Input value={editType.name} onChange={e => setEditType(s => ({ ...s, name: e.target.value }))} className="h-8" data-testid="type-rename-input" />
                      <Button size="sm" className="h-8" onClick={() => patchType(t, { name: editType.name })} data-testid="type-rename-save">Save</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditType(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{t.name}</span>
                      {t.built_in && <Badge variant="outline" className="text-[10px]">Built-in</Badge>}
                      {t.photo_trigger && <Badge className="bg-fuchsia-600 text-[10px]"><Camera className="h-3 w-3 mr-1" />Photo trigger</Badge>}
                      {!t.is_active && <Badge className="bg-slate-400 text-[10px]">Inactive</Badge>}
                      <span className="text-xs text-muted-foreground">· {t.usage_count} mapping(s)</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer" title="Photography trigger">
                    <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                    <Switch checked={!!t.photo_trigger} onCheckedChange={(v) => patchType(t, { photo_trigger: v })} data-testid={`type-photo-${t.type_id}`} />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer" title="Active">
                    <span className="text-muted-foreground">Active</span>
                    <Switch checked={!!t.is_active} onCheckedChange={(v) => patchType(t, { is_active: v })} data-testid={`type-active-${t.type_id}`} />
                  </label>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditType({ type_id: t.type_id, name: t.name })} data-testid={`type-edit-${t.type_id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" disabled={t.built_in || t.usage_count > 0} onClick={() => removeType(t)} title={t.built_in ? 'Built-in type' : (t.usage_count > 0 ? 'In use — deactivate instead' : 'Delete')} data-testid={`type-delete-${t.type_id}`}><Trash2 className={`h-4 w-4 ${t.built_in || t.usage_count > 0 ? 'text-muted-foreground' : 'text-red-600'}`} /></Button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-4 space-y-3">
            <Label className="text-sm font-semibold">Add new type</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input placeholder="e.g. Transport, Security, Hospitality" value={newType.name} onChange={e => setNewType(s => ({ ...s, name: e.target.value }))} data-testid="new-type-name" />
              <label className="flex items-center gap-2 text-sm whitespace-nowrap px-1">
                <Switch checked={newType.photo_trigger} onCheckedChange={(v) => setNewType(s => ({ ...s, photo_trigger: v }))} data-testid="new-type-photo" />
                Photo trigger
              </label>
              <Button onClick={addType} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-type-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTypesOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All {label}</SelectItem>
      {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);
